import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "@shared/common/ThemeContext";
import { Button } from "../../../shared/common/Button";
import { fetchAuth } from "../../../shared/common/api";
import SurveyEditor from "../../../shared/survey/SurveyEditor";
import { Tooltip } from "../../../shared/common/Tooltip";
import { formatLocalDate } from "../../../shared/common/datetimeUtils";

const MAX_ACTIVE_SURVEYS = 3;
const REQUIRED_ACTIVE_SURVEYS = 3;
const PERIODS = ["morning", "afternoon", "post_session"];
const TOGGLEABLE_PERIODS = ["morning", "afternoon"];
const PERIOD_LABEL = {
  morning: "Morning",
  afternoon: "Afternoon",
  post_session: "Post-session",
};

const toSurveyId = (value) =>
  value === null || value === undefined || value === "" ? null : String(value);

const hasDuplicateAssignments = (assignments) => {
  const seen = new Set();
  for (const period of PERIODS) {
    const surveyId = toSurveyId(assignments[period]);
    if (!surveyId) continue;
    if (seen.has(surveyId)) return true;
    seen.add(surveyId);
  }
  return false;
};

const SurveyPage = () => {
  const { isDarkMode } = useTheme();
  const [surveys, setSurveys] = useState([]);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [settings, setSettings] = useState({
    internalStats: true,
    externalStats: true,
    movesenseEnabled: false,
    hooperPeriods: [],
    surveyAssignments: { morning: null, afternoon: null, post_session: null },
  });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState(null);
  const [progressCollapsed, setProgressCollapsed] = useState(true);

  const activeSurveys = useMemo(
    () => surveys.filter((s) => s.is_active),
    [surveys]
  );
  const atMaxActive = activeSurveys.length >= MAX_ACTIVE_SURVEYS;
  const hasRequiredActiveSurveys = activeSurveys.length >= REQUIRED_ACTIVE_SURVEYS;

  const normalizeHooperPeriods = useCallback((periods) => {
    const src = Array.isArray(periods) ? periods : [];
    const ordered = TOGGLEABLE_PERIODS.filter((p) => src.includes(p));
    return ordered;
  }, []);

  const normalizeAssignments = useCallback((a) => {
    const out = { morning: null, afternoon: null, post_session: null };
    if (a && typeof a === "object") {
      for (const p of PERIODS) {
        out[p] = toSurveyId(a[p]);
      }
    }
    return out;
  }, []);

  const scheduleAssignments = useMemo(
    () => normalizeAssignments(settings.surveyAssignments),
    [settings.surveyAssignments, normalizeAssignments]
  );

  const hasDistinctAssignments = useMemo(
    () => !hasDuplicateAssignments(scheduleAssignments),
    [scheduleAssignments]
  );

  const progressSteps = useMemo(() => {
    const morningAssigned = !!scheduleAssignments.morning;
    const afternoonAssigned = !!scheduleAssignments.afternoon;
    const postSessionAssigned = !!scheduleAssignments.post_session;
    const morningEnabled = settings.hooperPeriods.includes("morning") && morningAssigned;
    const allAssigned = PERIODS.every((period) => !!scheduleAssignments[period]);

    return [
      {
        key: "create-three",
        label: `Create ${REQUIRED_ACTIVE_SURVEYS} active surveys`,
        done: hasRequiredActiveSurveys,
      },
      { key: "assign-morning", label: "Assign a survey to morning", done: morningAssigned },
      { key: "enable-morning", label: "Enable morning slot", done: morningEnabled },
      { key: "assign-afternoon", label: "Assign a survey to afternoon", done: afternoonAssigned },
      { key: "assign-post", label: "Assign a survey to post-session", done: postSessionAssigned },
      {
        key: "distinct",
        label: "Use distinct surveys per category",
        done: allAssigned && hasDistinctAssignments,
      },
    ];
  }, [hasDistinctAssignments, hasRequiredActiveSurveys, scheduleAssignments, settings.hooperPeriods]);

  const completedSteps = progressSteps.filter((step) => step.done).length;
  const progressPercent = Math.round((completedSteps / progressSteps.length) * 100);

  const refreshSurveys = useCallback(async () => {
    try {
      const res = await fetchAuth(`/surveys`);
      if (res.ok) {
        const list = await res.json();
        setSurveys(Array.isArray(list) ? list : []);
      }
    } catch {
      /* no-op */
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const res = await fetchAuth(`/clubs/mobile-stats-settings`);
      if (res.ok) {
        const data = await res.json();
        const normalizedAssignmentMap = normalizeAssignments(data.surveyAssignments);
        const normalizedPeriods = normalizeHooperPeriods(data.hooperPeriods).filter(
          (period) => !!normalizedAssignmentMap[period]
        );
        setSettings({
          internalStats: !!data.internalStats,
          externalStats: !!data.internalStats && !!data.externalStats,
          movesenseEnabled: !!data.movesenseEnabled,
          hooperPeriods: normalizedPeriods,
          surveyAssignments: normalizedAssignmentMap,
        });
      }
    } catch {
      /* no-op */
    }
  }, [normalizeHooperPeriods, normalizeAssignments]);

  useEffect(() => {
    refreshSurveys();
    refreshSettings();
  }, [refreshSurveys, refreshSettings]);

  const saveSettings = async (next) => {
    try {
      const normalizedPeriods = normalizeHooperPeriods(next.hooperPeriods);
      const normalizedAssignmentMap = normalizeAssignments(next.surveyAssignments);
      const payload = {
        internalStats: next.internalStats,
        externalStats: next.internalStats ? next.externalStats : false,
        movesenseEnabled: !!next.movesenseEnabled,
        hooperPeriods: normalizedPeriods,
        surveyAssignments: normalizedAssignmentMap,
      };
      const response = await fetchAuth(`/clubs/mobile-stats-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update settings");
      const saved = await response.json();
      const savedAssignments = normalizeAssignments(saved.surveyAssignments);
      const savedPeriods = normalizeHooperPeriods(saved.hooperPeriods).filter(
        (period) => !!savedAssignments[period]
      );
      setSettings({
        internalStats: !!saved.internalStats,
        externalStats: !!saved.internalStats && !!saved.externalStats,
        movesenseEnabled: !!saved.movesenseEnabled,
        hooperPeriods: savedPeriods,
        surveyAssignments: savedAssignments,
      });
      setError(null);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const validateScheduleSettings = useCallback(
    (next) => {
      const normalizedPeriods = normalizeHooperPeriods(next.hooperPeriods);
      const normalizedAssignmentMap = normalizeAssignments(next.surveyAssignments);

      for (const period of normalizedPeriods) {
        if (!normalizedAssignmentMap[period]) {
          return {
            ok: false,
            message: `Cannot enable ${PERIOD_LABEL[period]} without an assigned survey.`,
          };
        }
      }

      if (hasDuplicateAssignments(normalizedAssignmentMap)) {
        return {
          ok: false,
          message: "A survey cannot be assigned to multiple categories.",
        };
      }

      return {
        ok: true,
        nextPeriods: normalizedPeriods,
        nextAssignments: normalizedAssignmentMap,
      };
    },
    [normalizeAssignments, normalizeHooperPeriods]
  );

  const saveScheduleSettings = async (next) => {
    const validation = validateScheduleSettings(next);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    await saveSettings({
      ...next,
      hooperPeriods: validation.nextPeriods,
      surveyAssignments: validation.nextAssignments,
    });
  };

  const togglePeriod = (period) => {
    const enabled = settings.hooperPeriods.includes(period);
    const assigned = settings.surveyAssignments[period];

    if (!enabled && !assigned) {
      setError(`Assign a survey to ${PERIOD_LABEL[period]} before enabling it.`);
      return;
    }

    const cur = new Set(settings.hooperPeriods);
    if (cur.has(period)) cur.delete(period);
    else cur.add(period);
    const next = Array.from(cur);
    saveScheduleSettings({ ...settings, hooperPeriods: next });
  };

  const assignSurveyToPeriod = (period, surveyId) => {
    const normalizedSurveyId = toSurveyId(surveyId);

    if (normalizedSurveyId) {
      const conflictPeriod = PERIODS.find(
        (p) => p !== period && settings.surveyAssignments[p] === normalizedSurveyId
      );

      if (conflictPeriod) {
        setError(
          `This survey is already assigned to ${PERIOD_LABEL[conflictPeriod]}. Choose another one.`
        );
        return;
      }
    }

    if (!normalizedSurveyId && settings.hooperPeriods.includes(period)) {
      setError(`Disable ${PERIOD_LABEL[period]} before removing its survey assignment.`);
      return;
    }

    const next = {
      ...settings,
      surveyAssignments: {
        ...settings.surveyAssignments,
        [period]: normalizedSurveyId,
      },
    };
    saveScheduleSettings(next);
  };

  const toggleShowData = () => {
    saveSettings({
      ...settings,
      internalStats: !settings.internalStats,
      externalStats: !settings.internalStats ? false : settings.externalStats,
    });
  };

  const toggleShowTwin = () => {
    if (!settings.internalStats) return;
    saveSettings({ ...settings, externalStats: !settings.externalStats });
  };

  const toggleMovesense = () => {
    saveSettings({ ...settings, movesenseEnabled: !settings.movesenseEnabled });
  };

  const openCreateEditor = () => {
    if (atMaxActive) {
      setError(`Maximum ${MAX_ACTIVE_SURVEYS} active surveys reached. Delete or deactivate one first.`);
      return;
    }
    setEditingSurvey(null);
    setIsEditorOpen(true);
  };

  const openEditEditor = (survey) => {
    setEditingSurvey(survey);
    setIsEditorOpen(true);
  };

  const deleteSurvey = async (survey) => {
    try {
      const response = await fetchAuth(`/surveys/${survey.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Failed to delete survey");
      const assignments = { ...settings.surveyAssignments };
      const nextPeriods = new Set(settings.hooperPeriods);
      const deletedId = String(survey.id);
      let touched = false;

      for (const p of PERIODS) {
        if (assignments[p] === deletedId) {
          assignments[p] = null;
          touched = true;
          if (TOGGLEABLE_PERIODS.includes(p)) {
            nextPeriods.delete(p);
          }
        }
      }

      if (touched) {
        await saveSettings({
          ...settings,
          hooperPeriods: Array.from(nextPeriods),
          surveyAssignments: assignments,
        });
      }

      await refreshSurveys();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setPendingDelete(null);
    }
  };

  const themeClasses = isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800";
  const cardClasses = isDarkMode ? "bg-gray-800" : "bg-white";
  const subtle = isDarkMode ? "text-gray-400" : "text-gray-500";
  const progressBorder = isDarkMode ? "border-gray-700" : "border-slate-200";

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 flex flex-col w-full ${themeClasses}`}>
      <div className="fixed top-4 right-4 z-40 w-80 max-w-[calc(100vw-1rem)]">
        <div className={`rounded-xl border shadow-lg ${cardClasses} ${progressBorder}`}>
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <p className="text-sm font-semibold">Survey setup progress</p>
              <p className={`text-xs ${subtle}`}>
                {completedSteps}/{progressSteps.length} steps done
              </p>
            </div>
            <button
              type="button"
              onClick={() => setProgressCollapsed((value) => !value)}
              aria-label={progressCollapsed ? "Open progress panel" : "Close progress panel"}
              className={`p-1 rounded transition-colors ${
                isDarkMode ? "hover:bg-gray-700" : "hover:bg-slate-100"
              }`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className={`h-4 w-4 transition-transform duration-200 ${
                  progressCollapsed ? "-rotate-90" : "rotate-0"
                }`}
                aria-hidden="true"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {!progressCollapsed && (
            <div className="px-3 pb-3">
              <div className={`h-2 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-slate-200"}`}>
                <div
                  className="h-2 rounded-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="mt-3 space-y-1.5 text-sm">
                {progressSteps.map((step) => (
                  <div key={step.key} className="flex items-center gap-2">
                    <span className={step.done ? "text-emerald-500" : subtle}>
                      {step.done ? "✓" : "○"}
                    </span>
                    <span className={step.done ? "" : subtle}>{step.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <h1 className="text-4xl font-bold mb-6 text-center flex items-center justify-center">
        Survey Management
        <Tooltip
          text={`A club can have up to ${MAX_ACTIVE_SURVEYS} active surveys. Morning/afternoon are scheduled here. Post-session survey is assignment-only and activated when starting an RPE session.`}
          className="ml-2"
        >
          <div className="w-6 h-6 flex items-center justify-center bg-gray-300 text-gray-800 rounded-full cursor-pointer hover:bg-gray-400">
            <span className="text-sm font-bold">i</span>
          </div>
        </Tooltip>
      </h1>

      {error && (
        <div className="max-w-3xl mx-auto mb-4 px-4 py-2 rounded bg-red-100 text-red-800 text-sm">
          {error}
          <button className="ml-3 underline" onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      <div className="flex justify-center mb-6 gap-3">
        <Button
          onClick={openCreateEditor}
          disabled={atMaxActive}
          className={atMaxActive ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}
        >
          ➕ New Survey ({activeSurveys.length}/{MAX_ACTIVE_SURVEYS})
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        {surveys.length === 0 && (
          <div className={`col-span-full text-center ${subtle}`}>No survey yet. Create your first one.</div>
        )}
        {surveys.map((s) => {
          const surveyId = String(s.id);
          const assignedTo = PERIODS.filter((p) => settings.surveyAssignments[p] === surveyId);
          return (
            <div
              key={s.id}
              className={`group relative overflow-hidden rounded-2xl border p-5 shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl ${cardClasses} ${
                isDarkMode ? "border-gray-700/80" : "border-slate-200"
              }`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 ${s.is_active ? "bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500" : "bg-gradient-to-r from-slate-400 to-slate-500"}`} />

              <div className="flex items-start justify-between gap-3">
                <button
                  onClick={() => openEditEditor(s)}
                  className="min-w-0 text-left"
                  title="Open survey editor"
                >
                  <h2
                    className={`text-lg font-semibold truncate transition-colors ${
                      isDarkMode ? "text-slate-100 group-hover:text-cyan-300" : "text-slate-900 group-hover:text-blue-700"
                    }`}
                  >
                    {s.name || "(untitled)"}
                  </h2>
                  <p className={`text-xs ${subtle} mt-0.5`}>
                    {formatLocalDate(s.created_at)} · v{s.version}
                  </p>
                </button>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    s.is_active ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"
                  }`}
                >
                  {s.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              <p className={`text-xs ${subtle} mt-3`}>
                {Array.isArray(s.questions) ? s.questions.length : 0} questions
              </p>

              {assignedTo.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {assignedTo.map((p) => (
                    <span key={p} className="px-2 py-0.5 rounded text-xs bg-emerald-600 text-white">
                      {PERIOD_LABEL[p]}
                    </span>
                  ))}
                </div>
              )}

              {assignedTo.length === 0 && (
                <div className={`mt-3 inline-flex items-center rounded-full px-2 py-0.5 text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-slate-100 text-slate-600"}`}>
                  Not assigned
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mt-4">
                <button
                  onClick={() => openEditEditor(s)}
                  className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
                >
                  Edit survey
                </button>
                <button
                  onClick={() => setPendingDelete(s)}
                  className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 max-w-3xl mx-auto w-full">
        <h2 className="text-2xl font-bold mb-4">Mobile App Settings</h2>
        <div className={`p-5 rounded-lg ${cardClasses}`}>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={settings.internalStats} onChange={toggleShowData} />
              <span>Display data (internal + external)</span>
            </label>

            <label className={`flex items-center space-x-2 ${!settings.internalStats ? "opacity-60" : ""}`}>
              <input
                type="checkbox"
                checked={settings.internalStats && settings.externalStats}
                onChange={toggleShowTwin}
                disabled={!settings.internalStats}
              />
              <span>Display digital twin (3D)</span>
            </label>

            {!settings.internalStats && (
              <p className={`text-sm ${subtle}`}>The digital twin requires the display of data.</p>
            )}

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={!!settings.movesenseEnabled}
                onChange={toggleMovesense}
              />
              <span>Enable Movesense (sensor sessions)</span>
            </label>
            <p className={`text-sm ${subtle}`}>
              When enabled, the survey mobile experience shows Movesense pairing, sessions, and the sensor hub.
            </p>
          </div>

          <div className="pt-5 mt-5 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold mb-1">Daily survey schedule</p>
            <p className={`text-xs ${subtle} mb-3`}>
              Pick active slots for morning/afternoon and assign one distinct survey per slot.
              Post-session is assignment-only and enabled when launching an RPE session.
            </p>

            <div className="space-y-3">
              {TOGGLEABLE_PERIODS.map((p) => {
                const enabled = settings.hooperPeriods.includes(p);
                const assigned = settings.surveyAssignments[p] || "";
                const toggleDisabled = !enabled && !assigned;
                return (
                  <div
                    key={p}
                    className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded border ${
                      enabled
                        ? "border-emerald-500/40"
                        : isDarkMode ? "border-gray-700" : "border-gray-300"
                    }`}
                  >
                    <label className="flex items-center gap-2 min-w-[160px]">
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={toggleDisabled}
                        onChange={() => togglePeriod(p)}
                      />
                      <span className="font-medium">{PERIOD_LABEL[p]}</span>
                    </label>

                    <select
                      disabled={activeSurveys.length === 0}
                      value={assigned}
                      onChange={(e) => assignSurveyToPeriod(p, e.target.value || null)}
                      className={`flex-1 px-2 py-1.5 rounded border text-sm ${
                        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                      } ${activeSurveys.length === 0 ? "opacity-50" : ""}`}
                    >
                      <option value="">— No survey —</option>
                      {activeSurveys.map((s) => (
                        <option
                          key={s.id}
                          value={String(s.id)}
                          disabled={PERIODS.some(
                            (periodName) =>
                              periodName !== p && settings.surveyAssignments[periodName] === String(s.id)
                          )}
                        >
                          {s.name || "(untitled)"}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}

              <div
                className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded border ${
                  isDarkMode ? "border-gray-700" : "border-gray-300"
                }`}
              >
                <div className="min-w-[160px]">
                  <p className="font-medium">{PERIOD_LABEL.post_session}</p>
                  <p className={`text-xs ${subtle}`}>
                    Assignment only. Activation depends on each RPE session start.
                  </p>
                </div>

                <select
                  disabled={activeSurveys.length === 0}
                  value={settings.surveyAssignments.post_session || ""}
                  onChange={(e) => assignSurveyToPeriod("post_session", e.target.value || null)}
                  className={`flex-1 px-2 py-1.5 rounded border text-sm ${
                    isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                  } ${activeSurveys.length === 0 ? "opacity-50" : ""}`}
                >
                  <option value="">— No survey —</option>
                  {activeSurveys.map((s) => (
                    <option
                      key={s.id}
                      value={String(s.id)}
                      disabled={PERIODS.some(
                        (periodName) =>
                          periodName !== "post_session" &&
                          settings.surveyAssignments[periodName] === String(s.id)
                      )}
                    >
                      {s.name || "(untitled)"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditorOpen && (
        <SurveyEditor
          survey={editingSurvey}
          onClose={() => { setIsEditorOpen(false); setEditingSurvey(null); }}
          onSave={async () => {
            await refreshSurveys();
            setIsEditorOpen(false);
            setEditingSurvey(null);
          }}
        />
      )}

      {pendingDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className={`p-6 rounded-lg shadow-lg w-full max-w-md ${cardClasses}`}>
            <h2 className="text-2xl font-bold mb-3 text-center">Delete "{pendingDelete.name}"?</h2>
            <p className="text-center mb-6">This action cannot be undone.</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => deleteSurvey(pendingDelete)}
                className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </button>
              <button
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 rounded-lg font-medium bg-gray-600 hover:bg-gray-700 text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyPage;
